import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar, FileText, Clock, ArrowLeft, ArrowRight, ExternalLink,
  Target, Newspaper, ChevronDown, ChevronUp, BookOpen,
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';

const API = process.env.REACT_APP_BACKEND_URL;

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

/* ─── Article Card ─── */
const ArticleCard = ({ article, index }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
    <Link to={`/blog/${article.slug}`} className="block group" data-testid={`mandate-article-${article.slug}`}>
      <div className="bg-white rounded-xl border border-slate-200 p-5 hover:border-[#0066CC]/40 hover:shadow-sm transition-all">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-slate-800 group-hover:text-[#0066CC] transition-colors mb-1">{article.title}</h4>
            {article.excerpt && <p className="text-xs text-slate-400 line-clamp-2">{article.excerpt}</p>}
            <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
              <span>{article.reading_time} min read</span>
              <span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : ''}</span>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#0066CC] flex-shrink-0 mt-1 transition-colors" />
        </div>
      </div>
    </Link>
  </motion.div>
);

/* ─── Angle Section ─── */
const AngleSection = ({ group, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`angle-section-${group.angle_id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#0066CC]" />
          <span className="text-sm font-semibold text-slate-700">{group.angle_name}</span>
          <span className="text-xs text-slate-400 bg-white px-2 py-0.5 rounded-full">{group.articles.length}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="p-4 space-y-2">
          {group.articles.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}
        </div>
      )}
    </div>
  );
};

/* ─── News Item ─── */
const NewsItem = ({ item }) => (
  <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0">
    <Newspaper className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
    <div className="flex-1 min-w-0">
      <p className="text-sm text-slate-700 font-medium">{item.title}</p>
      <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
        <span>{item.source_name}</span>
        {item.source_url && <a href={item.source_url} target="_blank" rel="noreferrer" className="text-[#0066CC] hover:underline flex items-center gap-0.5"><ExternalLink className="w-3 h-3" /> Source</a>}
      </div>
    </div>
  </div>
);

/* ─── Tracker Status ─── */
const TrackerStatus = ({ data }) => {
  if (!data?.length) return null;
  return (
    <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Target className="w-4 h-4 text-[#0066CC]" /> Regulatory status</h3>
      <div className="space-y-3">
        {data.map((t, i) => (
          <div key={i} className="bg-white rounded-lg p-4 border border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-800">{t.country_name} — {t.tracker_category}</span>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: t.status_color + '20', color: t.status_color }}>{t.status_label}</span>
            </div>
            {t.layer1?.summary && <p className="text-xs text-slate-500">{t.layer1.summary.slice(0, 200)}...</p>}
            <div className="flex items-center gap-2 mt-2">
              <Link to={`/tracker/${t.tracker_category}/${t.country_code}`} className="text-xs text-[#0066CC] hover:underline flex items-center gap-1">
                Full tracker details <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Main Page ─── */
const MandatePage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    fetch(`${API}/api/mandates/${slug}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(d => setData(d))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <PageLayout><div className="flex items-center justify-center py-32"><div className="w-6 h-6 border-2 border-[#0066CC] border-t-transparent rounded-full animate-spin" /></div></PageLayout>
  );

  if (error || !data) return (
    <PageLayout>
      <div className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Mandate not found</h1>
        <p className="text-slate-500 mb-6">This mandate page doesn't exist or isn't active yet.</p>
        <Link to="/mandates" className="text-[#0066CC] hover:underline">Back to all mandates</Link>
      </div>
    </PageLayout>
  );

  const deadline = daysUntil(data.mandate_deadline);
  const articlesByAngle = data.articles_by_angle || [];
  const relatedNews = data.related_news || [];
  const trackerData = data.tracker_data || [];

  return (
    <PageLayout>
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Breadcrumb */}
        <Link to="/mandates" className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#0066CC] mb-6" data-testid="mandates-back-link">
          <ArrowLeft className="w-4 h-4" /> All mandates
        </Link>

        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-testid="mandate-title">{data.name}</h1>
          <p className="text-base text-slate-500 max-w-3xl mb-5">{data.public_summary || `Comprehensive coverage of ${data.name} — regulatory updates, technical guidance, and practical readiness resources.`}</p>

          {/* Stats bar */}
          <div className="flex items-center gap-6 flex-wrap">
            {deadline !== null && (
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${deadline < 90 ? 'bg-amber-50 border-amber-200 text-amber-700' : deadline < 180 ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
                <Clock className="w-4 h-4" />
                <span className="text-sm font-semibold">{deadline > 0 ? `${deadline} days` : 'Deadline passed'}</span>
                <span className="text-xs opacity-70">until mandate deadline</span>
              </div>
            )}
            {data.mandate_deadline && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Calendar className="w-4 h-4" />
                {new Date(data.mandate_deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <FileText className="w-4 h-4" />
              {data.article_count} article{data.article_count !== 1 ? 's' : ''}
            </div>
          </div>
        </motion.div>

        {/* Content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main — articles by angle */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-lg font-bold text-slate-800 mb-2">Coverage by topic</h2>
            {articlesByAngle.length === 0 ? (
              <div className="bg-slate-50 rounded-xl p-8 text-center">
                <FileText className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Articles are being generated. Check back soon for in-depth coverage.</p>
              </div>
            ) : (
              articlesByAngle.map((g, i) => <AngleSection key={g.angle_id} group={g} defaultOpen={i === 0} />)
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* CTA */}
            <div className="bg-[#0066CC] rounded-xl p-6 text-white">
              <h3 className="text-lg font-bold mb-2">Need help with compliance?</h3>
              <p className="text-sm text-blue-100 mb-4">
                Talk to our team about how Taxera can help you meet this mandate's requirements.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-[#0066CC] px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors" data-testid="mandate-cta">
                Get in touch <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Tracker */}
            <TrackerStatus data={trackerData} />

            {/* Related news */}
            {relatedNews.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Newspaper className="w-4 h-4 text-[#0066CC]" /> Latest news</h3>
                <div>{relatedNews.slice(0, 6).map(n => <NewsItem key={n.id} item={n} />)}</div>
                <Link to="/news" className="block text-xs text-[#0066CC] hover:underline mt-3 text-center">View all news</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default MandatePage;
